import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "@/components/Header";
import DrumTabViewer from "@/components/DrumTabViewer";
import VirtualDrumKit from "@/components/VirtualDrumKit";
import Metronome from "@/components/Metronome";
import AuthModal from "@/components/AuthModal";
import PricingModal from "@/components/PricingModal";
import TeamSheet from "@/components/TeamSheet";
import ReferralModal from "@/components/ReferralModal";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2, AlertTriangle } from "lucide-react";

export default function SharedTab() {
  const { id } = useParams();
  const { user } = useAuth();
  const [tab, setTab] = useState(null);
  const [error, setError] = useState(false);
  const [sharedNote, setSharedNote] = useState(null);
  const [sharedSticking, setSharedSticking] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [metroOpen, setMetroOpen] = useState(false);
  const [pricingOpen, setPricingOpen] = useState(false);
  const [teamOpen, setTeamOpen] = useState(false);
  const [referralOpen, setReferralOpen] = useState(false);
  const isPro = !!(user && user.is_pro);
  const handleUpgrade = () => (user ? setPricingOpen(true) : setAuthOpen(true));

  useEffect(() => {
    api
      .get(`/share/${id}`)
      .then((r) => { setTab(r.data.tab); setSharedNote(r.data.teacher_note); setSharedSticking(r.data.sticking || null); })
      .catch(() => setError(true));
  }, [id]);

  return (
    <div className="min-h-screen bg-[#0b0c10] text-slate-100">
      <Header
        onOpenAuth={() => setAuthOpen(true)}
        onOpenMetronome={() => setMetroOpen(true)}
        onOpenLibrary={() => {}}
        onOpenPricing={() => setPricingOpen(true)}
        onOpenTeam={() => setTeamOpen(true)}
        onOpenReferral={() => setReferralOpen(true)}
        libraryCount={0}
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
        <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#ffab00]/30 bg-[#ffab00]/5 text-xs font-tab tracking-widest uppercase amber-text">
            <Sparkles className="w-3.5 h-3.5" />
            Shared Tab
          </div>
          <Link to="/">
            <Button className="bg-[#00f0ff] text-black hover:bg-[#33f4ff] font-semibold" data-testid="create-own-button">
              <Sparkles className="w-4 h-4 mr-2" /> Make your own
            </Button>
          </Link>
        </div>

        {error ? (
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-12 text-center">
            <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-[#FF3366]/70" />
            <h3 className="font-heading text-2xl uppercase tracking-tight">Tab not found</h3>
            <p className="text-slate-500 text-sm mt-2">This share link is invalid or has expired.</p>
          </div>
        ) : !tab ? (
          <div className="flex items-center justify-center py-24 text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading tab…
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <DrumTabViewer tab={tab} user={user} isPro={isPro} onUpgrade={handleUpgrade} onRequireAuth={() => setAuthOpen(true)} onSaved={() => {}} sharedNote={sharedNote} sharedSticking={sharedSticking} />
            </div>
            <div className="lg:col-span-1">
              <VirtualDrumKit />
            </div>
          </div>
        )}
      </div>

      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
      <Metronome open={metroOpen} onOpenChange={setMetroOpen} />
      <PricingModal open={pricingOpen} onOpenChange={setPricingOpen} onRequireAuth={() => setAuthOpen(true)} />
      <TeamSheet open={teamOpen} onOpenChange={setTeamOpen} />
      <ReferralModal open={referralOpen} onOpenChange={setReferralOpen} />
    </div>
  );
}
